import React from 'react';
import { 
  Box, 
  Grid,
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableRow,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { lightColours } from '../../styles/colours';

// Helper function to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};


const ComparisonResults = ({ comparisonData }) => {
  const { salaryA, salaryB, betterOption } = comparisonData;

  const SalaryResultsSection = ({ title, data, isBetter = false }) => (
    <Paper elevation={0} sx={{ 
      p: 3, 
      position: 'relative', 
      border: isBetter ? '3px solid #4caf50' : '1px solid #e0e0e0'
    }}>
      {isBetter && (
        <Chip 
          icon={<TrendingUpIcon />}
          label="Better Option"
          color="success"
          sx={{ position: 'absolute', top: 16, right: 16 }}
        />
      )}
      
      <Typography variant="h5" component="h3" gutterBottom sx={{ 
        fontWeight: 'bold',
        color: lightColours.darkBlue,
        mb: 3
      }}>
        {title}
      </Typography>

      <TableContainer>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell><strong>Gross Annual Salary</strong></TableCell>
              <TableCell align="right">{formatCurrency(data.totalGrossIncome)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Take-Home Pay (Annual)</strong></TableCell>
              <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                {formatCurrency(data.takeHome)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Take-Home Pay (Monthly)</TableCell>
              <TableCell align="right">{formatCurrency(data.takeHome / 12)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Income Tax</TableCell>
              <TableCell align="right">{formatCurrency(data.taxPaid)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>National Insurance</TableCell>
              <TableCell align="right">{formatCurrency(data.nationalInsurance)}</TableCell>
            </TableRow>
            {data.pensionDeduction > 0 && (
              <TableRow>
                <TableCell>Pension Contribution</TableCell>
                <TableCell align="right">{formatCurrency(data.pensionDeduction)}</TableCell>
              </TableRow>
            )}
            {(data.studentLoanRepayment || 0) > 0 && (
              <TableRow>
                <TableCell>Student Loan Repayment</TableCell>
                <TableCell align="right">{formatCurrency(data.studentLoanRepayment)}</TableCell>
              </TableRow>
            )}
            <TableRow sx={{ borderTop: '2px solid #e0e0e0' }}>
              <TableCell><strong>Total Compensation Value</strong></TableCell>
              <TableCell align="right" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                {formatCurrency(data.totalCompensation)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );


  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h4" component="h2" gutterBottom sx={{ 
        textAlign: 'center',
        fontWeight: 'bold',
        mb: 4
      }}>
        Salary Comparison Results
      </Typography>

      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 6 }}>
          <SalaryResultsSection 
            title="Current Salary Results"
            data={salaryA}
            isBetter={betterOption === 'A'}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <SalaryResultsSection 
            title="New Salary Results"
            data={salaryB}
            isBetter={betterOption === 'B'}
          />
        </Grid>
      </Grid>

      {/* Better Option Explanation */}
      <Accordion elevation={0} sx={{ mt: 4, border: '1px solid #e0e0e0' }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="better-option-explanation-content"
          id="better-option-explanation-header"
          sx={{ backgroundColor: '#f8f9fa' }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: lightColours.darkBlue }}>
            How We Determine the "Better Option"
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3 }}>
          <Typography variant="body1" paragraph>
            Our comparison tool identifies the "better option" based on <strong>total compensation value</strong> - 
            your take-home pay plus the value of pension contributions made on your behalf.
          </Typography>
          
          <Typography variant="body2" paragraph sx={{ backgroundColor: '#fff3cd', p: 2, borderRadius: 1, mb: 2 }}>
            <strong>💡 Why Total Compensation Matters:</strong> A salary with higher pension contributions 
            might have lower take-home pay but higher overall value due to the retirement savings benefit.
          </Typography>
          
          <Typography variant="body2" paragraph sx={{ mb: 2 }}>
            <strong>What we consider:</strong>
          </Typography>
          
          <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 1 } }}>
            <li>
              <Typography variant="body2">
                <strong>Income Tax:</strong> Calculated using current UK tax bands and rates
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>National Insurance:</strong> Both employee contributions and thresholds
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Pension Contributions:</strong> Auto-enrolment, salary sacrifice, or personal pensions
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Student Loan Repayments:</strong> All plan types (1, 2, 4, and Postgraduate)
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Overtime Pay:</strong> Additional earnings included in calculations
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>Scottish Tax Rates:</strong> Applied when Scottish residency is selected
              </Typography>
            </li>
          </Box>
          
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            <strong>Note:</strong> While take-home pay is the primary factor, consider other benefits like 
            pension matching, healthcare, flexible working, career development opportunities, and job security 
            when making your final decision.
          </Typography>
        </AccordionDetails>
      </Accordion>

    </Box>
  );
};

export default ComparisonResults;